import { ExternalMarketDataStrategyBase } from 'marketData/ExternalMarketDataStrategy';
import { MarketDataRequest } from 'marketData/MarketDataRequest';
import OpenFin from '@openfin/core';
import { InFunctionNames, OutFunctionNames } from './types';
import { BarData, TickData } from 'marketData/store/marketDataSlice';
import {
    buildLogger,
    Logger,
    LoggingService,
} from '@instinet-analytics/core-lib';

type Options = {
    clientChannel: OpenFin.ChannelProvider;
    identity: OpenFin.Identity;
    loggingService: LoggingService;
};

/*
 * If we are running in OpenFin, and the mktDataInterop query parameter is set,
 * we use this strategy for external market data,
 * which uses OpenFin channels to communicate with the host
 */
export class OpenFinApiExternalMarketDataStrategy extends ExternalMarketDataStrategyBase {
    private readonly clientChannel: OpenFin.ChannelProvider;
    private readonly identity: OpenFin.Identity;
    private logger: Logger;

    public constructor(options: Options) {
        super();
        this.logger = buildLogger(
            'OpenFinApiExternalMarketDataStrategy',
            options.loggingService
        );
        this.clientChannel = options.clientChannel;
        this.identity = options.identity;
        this.registerInMethods();
        this.logger.info('Created OpenFinApiExternalMarketDataStrategy');
    }

    public subscribe(request: MarketDataRequest): void {
        this.logger.info('Subscribing', {
            identity: this.identity,
            name: OutFunctionNames.SubscribeMarketData,
            request,
        });
        this.clientChannel.dispatch(
            this.identity!,
            OutFunctionNames.SubscribeMarketData,
            request
        );
    }

    public unsubscribe(request: MarketDataRequest): void {
        this.logger.info('Unsubscribing', {
            identity: this.identity,
            name: OutFunctionNames.SubscribeMarketData,
            request,
        });
        this.clientChannel.dispatch(
            this.identity!,
            OutFunctionNames.UnsubscribeMarketData,
            request
        );
    }

    private registerInMethods() {
        this.clientChannel.remove(InFunctionNames.SetMarketRefData);
        this.clientChannel.register(
            InFunctionNames.SetMarketRefData,
            async (unknownPayload: unknown, _identity) => {
                this.logger.info(
                    'Handing ' + InFunctionNames.SetMarketRefData,
                    unknownPayload
                );
                const payload = unknownPayload as {
                    symbol: string;
                    startTimestamp: number;
                    endTimestamp: number;
                    averageVolume: number;
                    pnc: number;
                };
                if (this.onRefDataReceivedHandler) {
                    this.onRefDataReceivedHandler({
                        bbSymbol: payload.symbol ?? '',
                        startTradingHoursTimestamp: payload.startTimestamp,
                        endTradingHoursTimestamp: payload.endTimestamp,
                        averageVolume: payload.averageVolume,
                        pnc: payload.pnc,
                    });
                }
            }
        );

        this.clientChannel.remove(InFunctionNames.SetMarketBarData);
        this.clientChannel.register(
            InFunctionNames.SetMarketBarData,
            async (unknownPayload: unknown, _identity) => {
                this.logger.info(
                    'Handing ' + InFunctionNames.SetMarketBarData,
                    unknownPayload
                );
                const payload = unknownPayload as {
                    data: BarData[];
                };
                if (this.onMarketBarDataReceivedHandler) {
                    this.onMarketBarDataReceivedHandler(payload.data);
                }
            }
        );

        this.clientChannel.remove(InFunctionNames.SetMarketTickData);
        this.clientChannel.register(
            InFunctionNames.SetMarketTickData,
            async (unknownPayload: unknown, _identity) => {
                this.logger.info(
                    'Handing ' + InFunctionNames.SetMarketTickData,
                    unknownPayload
                );
                const payload = unknownPayload as {
                    data: TickData[];
                };
                if (this.onMarketTickDataReceivedHandler) {
                    this.onMarketTickDataReceivedHandler(payload.data);
                }
            }
        );
    }
}
